import { computed, observable, runInAction } from "mobx"
import { toast } from "sonner"

import { batchInfo } from "@/lib/apiClient/client.ts"
import { formatErrorMessage, pluralize } from "@/lib/displayUtils.ts"
import { Model } from "@/lib/Model.ts"
import { isUserPrincipal } from "@/lib/std.ts"
import { validateEmail } from "@/lib/validateUtils.ts"
import { orgPermissionsProp } from "@/routes/projects/projectHooks.ts"

import type {
  CPrincipal,
  InvitationLandingContext,
  OrganizationMember,
  OrganizationMemberRole,
  TID
} from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { Queries } from "@/lib/queries.ts"
import type { OrgPermissionsProp } from "@/routes/projects/projectHooks.ts"

type OrganizationMemberView = OrganizationMember & {
  previousRole?: OrganizationMemberRole
  pendingDeletion?: boolean
}

type InvitationField = {
  email: string
  role: OrganizationMemberRole
  valid: boolean
  touched: boolean
}

export class InviteTeammatesDialogModel extends Model {
  readonly permissions: OrgPermissionsProp
  readonly members = observable.box<OrganizationMemberView[]>([])
  readonly invitations = observable.box<InvitationField[]>([])
  readonly error = observable.box<Error | null>(null)
  readonly currentUser = observable.box<CPrincipal | null>(null)
  readonly submitting = observable.box(false)

  readonly batchSize = 50

  readonly nextOffset = observable.box<string | null>(null)

  readonly hasMore = observable.box(true)

  private loadingNext = false

  readonly disabled = computed(() => {
    return this.submitting.get()
      || this.invitations.get().every(invitation => invitation.email.trim() === "")
      || this.invitations.get().some(invitation => !invitation.valid)
  })

  readonly addEmailField = () => {
    const updated: InvitationField[] = [...this.invitations.get(), this.emptyInvitation()]
    runInAction(() => this.invitations.set(updated))
  }

  readonly updateField = (index: number, field: Partial<InvitationField>) => {
    const updated = [...this.invitations.get()]
    const current = { ...updated[index], ...field }

    runInAction(() => {
      if ("email" in field) {
        current.valid = validateEmail(field.email ?? "")
      }

      updated[index] = current

      this.invitations.set(updated)
    })
  }

  readonly markTouched = (index: number, touched: boolean) => {
    const updated = [...this.invitations.get()]
    runInAction(() => {
      updated[index].touched = touched
      this.invitations.set(updated)
    })
  }

  readonly submitInvitations = async () => {
    if (!this.validate()) return Promise.resolve(false)

    runInAction(() => this.submitting.set(true))

    try {
      const validEmails = this.invitations.get()
        .filter(({ email }) => email.trim() !== "")

      const promises = validEmails
        .map(({ email, role }) =>
          this.queries.apiClient.createInvitation(email.trim(), null, null, role, this.landingContext).catch((e: Error) => {
            runInAction(() => this.error.set(e))
          })
        )

      await Promise.all(promises)

      if (this.error.get()) return Promise.resolve(false)

      toast.success(`Invitations sent to ${promises.length} email ${pluralize("address", promises.length)}`, { testId: "invite-success" })

      return Promise.resolve(true)
    } finally {
      runInAction(() => this.submitting.set(false))
    }
  }

  private emptyInvitation(): InvitationField {
    return { email: "", role: "Admin", valid: true, touched: false }
  }

  private readonly validate = (): boolean => {
    let valid = true
    const updated = this.invitations.get().map(entry => {
      const isValid = validateEmail(entry.email)
      if (!isValid) valid = false
      return { ...entry, valid: isValid }
    })
    runInAction(() => this.invitations.set(updated))
    return valid
  }

  readonly loadMembers = async (withLoader = true, append = true) => {
    if (this.loadingNext) return

    this.loadingNext = true

    if (withLoader) {
      runInAction(() => this.isLoading.set(true))
    }

    if (!append) {
      runInAction(() => {
        this.members.set([])
        this.nextOffset.set(null)
        this.hasMore.set(true)
      })
    }

    try {
      const batch = await this.queries.getOrganizationMembers(batchInfo(this.batchSize, this.nextOffset.get()))
      runInAction(() => {
        this.members.set([...this.members.get(), ...batch.data])
        this.nextOffset.set(batch.next)
        this.hasMore.set(batch.data.length === this.batchSize)
      })
    } finally {
      this.loadingNext = false
    }

    if (withLoader) {
      runInAction(() => this.isLoading.set(false))
    }
  }

  readonly setOrganizationMemberRole = async (profileId: TID, role: OrganizationMemberRole, index: number) => {
    const members = this.members.get()
    const member = members[index]
    if (!member) throw new Error(`Member not found at index ${index}`)

    runInAction(() => {
      member.previousRole = member.role
      member.role = role
    })

    try {
      await this.queries.apiClient.setOrganizationMemberRole(profileId, role)
      runInAction(() => {
        delete member.previousRole
      })
    } catch (error) {
      runInAction(() => {
        member.role = member.previousRole!
        delete member.previousRole
      })
      toast.error(`Failed to update role: ${formatErrorMessage(error, "Unknown error")}`)
    }
  }

  readonly removeOrganizationMember = async (profileId: TID) => {
    const members = this.members.get()
    const memberIndex = members.findIndex(m => m.profile.id === profileId)
    if (memberIndex < 0) throw new Error(`Member not found with profile ID ${profileId}`)

    const member = members[memberIndex]

    runInAction(() => {
      member.pendingDeletion = true
    })

    try {
      await this.queries.apiClient.removeOrganizationMember(profileId)
      runInAction(() => {
        const currentMembers = this.members.get()
        const currentIndex = currentMembers.findIndex(m => m.profile.id === profileId)
        if (currentIndex >= 0) {
          currentMembers.splice(currentIndex, 1)
        }
      })
    } catch (error) {
      runInAction(() => {
        delete member.pendingDeletion
      })
      toast.error(`Failed to remove teammate: ${formatErrorMessage(error, "Unknown error")}`)
    }
  }

  constructor(
    readonly context: OrgContext,
    private readonly queries: Queries,
    currentUser: CPrincipal,
    private readonly landingContext?: InvitationLandingContext
  ) {
    super("InviteTeammatesDialogModel")

    this.permissions = orgPermissionsProp(context.queries)
    this.currentUser.set(currentUser)

    void this.loadMembers()

    runInAction(() => {
      this.invitations.set(
        Array(1).fill(null).map(this.emptyInvitation)
      )
    })
  }

  readonly canChangeRole = (member: OrganizationMember): boolean => {
    const currentUser = this.currentUser.get()
    if (!currentUser) return true // Allow changes if current user not loaded yet

    // Prevent admin from demoting themselves to member
    if (isUserPrincipal(currentUser) && currentUser.details.user.id === member.profile.id && member.role === "Admin") {
      return false
    }

    return true
  }

  readonly isMe = (member: OrganizationMember): boolean => {
    const currentUser = this.currentUser.get()
    if (!currentUser) return false

    return isUserPrincipal(currentUser) && currentUser.details.user.id === member.profile.id
  }
}
